import { EditOutlined, FolderOpenOutlined, PlusOutlined } from "@ant-design/icons";
import { App, Button, Spin, Tag } from "antd";
import L from "leaflet";
import { useEffect, useMemo, useState } from "react";
import { MapContainer, Marker, Polygon, Popup, TileLayer, useMap } from "react-leaflet";
import { useNavigate } from "react-router-dom";
import { useEquipmentLive } from "@/modules/equipment/hooks/useEquipment";
import type { LiveEquipmentPosition } from "@/modules/equipment/types";
import { geoJsonToLeafletPositions, geometryToLatLngs } from "@/shared/map/geojson";
import {
	createCarIcon,
	EQUIPMENT_STATUS_COLORS as STATUS_COLORS,
	EQUIPMENT_STATUS_LABELS as STATUS_LABELS,
} from "@/shared/map/icons";
import { useTerritories } from "../hooks/useTerritories";
import type { Territory } from "../services/territory.service";
import { TerritoryFormModal } from "./TerritoryFormModal";

/** Distinct colors for territory polygons - each territory gets a different color */
const TERRITORY_COLORS = [
	"#1677ff", // blue
	"#f97316", // orange
	"#14b8a6", // teal
	"#8b5cf6", // purple
	"#ec4899", // pink
	"#22c55e", // green
	"#eab308", // yellow
	"#ef4444", // red
	"#6366f1", // indigo
	"#06b6d4", // cyan
	"#84cc16", // lime
	"#a855f7", // violet
];

function TerritoryPolygon({
	territory,
	colorIndex,
	isSelected,
	onSelect,
	children,
}: {
	territory: Territory & { coordinates: NonNullable<Territory["coordinates"]> };
	colorIndex: number;
	isSelected?: boolean;
	onSelect: (territory: Territory) => void;
	children: React.ReactNode;
}) {
	const map = useMap();
	const positions = geoJsonToLeafletPositions(territory.coordinates.coordinates);
	const boundsPositions = geometryToLatLngs(territory.coordinates);
	const color = TERRITORY_COLORS[colorIndex % TERRITORY_COLORS.length];

	const handleClick = () => {
		if (boundsPositions.length > 0) {
			const bounds = L.latLngBounds(boundsPositions);
			map.fitBounds(bounds, { padding: [20, 20], maxZoom: 18 });
		}
		onSelect(territory);
	};

	return (
		<Polygon
			positions={positions as any}
			pathOptions={{
				color: isSelected ? "#eab308" : color,
				fillColor: color,
				fillOpacity: isSelected ? 0.55 : 0.35,
				weight: isSelected ? 4 : 2,
			}}
			eventHandlers={{ click: handleClick }}
		>
			{children}
		</Polygon>
	);
}

function FitBounds({
	territoryPositions,
	equipmentPositions,
}: {
	territoryPositions: [number, number][];
	equipmentPositions: [number, number][];
}) {
	const map = useMap();
	const allPositions = useMemo(
		() => [...territoryPositions, ...equipmentPositions],
		[territoryPositions, equipmentPositions]
	);

	useEffect(() => {
		if (allPositions.length === 0) {
			return;
		}
		if (allPositions.length === 1) {
			map.setView(allPositions[0], 12);
			return;
		}
		map.fitBounds(L.latLngBounds(allPositions), {
			padding: [30, 30],
			maxZoom: 14,
		});
	}, [map, allPositions]);

	return null;
}

interface TerritoryLiveMapProps {
	organizationId: string;
}

export function TerritoryLiveMap({ organizationId }: TerritoryLiveMapProps) {
	const navigate = useNavigate();
	const { message } = App.useApp();
	const [parentStack, setParentStack] = useState<Array<{ id: string; name: string }>>([]);
	const [pendingParent, setPendingParent] = useState<{ id: string; name: string } | null>(null);
	const [selectedTerritory, setSelectedTerritory] = useState<Territory | null>(null);
	const [isCreateOpen, setIsCreateOpen] = useState(false);
	const [editingTerritory, setEditingTerritory] = useState<Territory | null>(null);
	const currentParentId = parentStack.at(-1)?.id;

	const { data: territoriesData, isLoading: isLoadingTerritories } = useTerritories({
		organizationId,
		page: 1,
		limit: 100,
		parentId: currentParentId,
	});
	const { data: equipmentData, isFetching: isFetchingEquipment } = useEquipmentLive({
		organizationId,
	});

	const territories = territoriesData?.data?.items ?? [];
	const equipment = equipmentData ?? [];
	const withPosition = equipment.filter(
		(
			e
		): e is LiveEquipmentPosition & {
			position: NonNullable<LiveEquipmentPosition["position"]>;
		} => e.position !== null
	);

	const territoryPositions = useMemo(
		() =>
			territories
				.filter(
					(
						t
					): t is Territory & {
						coordinates: NonNullable<Territory["coordinates"]>;
					} => !!t.coordinates?.coordinates?.length
				)
				.flatMap((t) => geometryToLatLngs(t.coordinates)),
		[territories]
	);
	const equipmentPositions: [number, number][] = withPosition.map((e) => [
		e.position.latitude,
		e.position.longitude,
	]);

	const onlineCount = equipment.filter((e) => e.status === "online").length;
	const offlineCount = equipment.filter((e) => e.status === "offline").length;
	const errorCount = equipment.filter((e) => e.status === "error").length;

	const hasContent = territoryPositions.length > 0 || equipmentPositions.length > 0;

	useEffect(() => {
		if (!pendingParent || isLoadingTerritories || currentParentId !== pendingParent.id) {
			return;
		}

		if (territories.length === 0) {
			setParentStack((prev) => prev.slice(0, -1));
			message.info(`"${pendingParent.name}" hududida ichki hudud topilmadi`);
		}

		setPendingParent(null);
	}, [pendingParent, isLoadingTerritories, currentParentId, territories.length, message]);

	const goRoot = () => {
		setParentStack([]);
	};

	const goBack = () => {
		setParentStack((prev) => prev.slice(0, -1));
	};

	const openChildren = (territory: Territory) => {
		setPendingParent({ id: territory.id, name: territory.name });
		setParentStack((prev) => [...prev, { id: territory.id, name: territory.name }]);
	};

	return (
		<div className="space-y-3">
			{/* Stats bar */}
			<div className="flex flex-wrap items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5">
				<Tag color="blue" className="m-0">
					Hududlar: {territories.length}
				</Tag>
				<Tag color="success" className="m-0">
					Online: {onlineCount}
				</Tag>
				<Tag color="default" className="m-0">
					Offline: {offlineCount}
				</Tag>
				<Tag color="error" className="m-0">
					Xato: {errorCount}
				</Tag>
				<span className="text-gray-400 text-xs">Texnikalar: {equipment.length}</span>
				<Button size="small" onClick={goRoot} disabled={parentStack.length === 0}>
					Asosiy hududlar
				</Button>
				<Button size="small" onClick={goBack} disabled={parentStack.length === 0}>
					Orqaga
				</Button>
				<span className="text-gray-500 text-xs">
					{parentStack.length === 0
						? "Daraja: Asosiy"
						: `Daraja: ${parentStack.map((item) => item.name).join(" / ")}`}
				</span>
				{selectedTerritory && (
					<Tag color="gold" closable onClose={() => setSelectedTerritory(null)}>
						Tanlangan: {selectedTerritory.name}
					</Tag>
				)}
				<Button
					type="primary"
					size="small"
					icon={<PlusOutlined />}
					onClick={() => setIsCreateOpen(true)}
				>
					Yangi hudud
				</Button>
				{(isLoadingTerritories || isFetchingEquipment) && <Spin size="small" />}
			</div>

			{/* Map */}
			<div className="h-[calc(100vh-220px)] min-h-[400px] w-full rounded-xl overflow-hidden border border-gray-200 shadow-sm">
				<MapContainer center={[41.56, 60.62]} zoom={7} className="h-full w-full" scrollWheelZoom>
					<TileLayer
						attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
						url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
					/>
					{hasContent && (
						<FitBounds
							territoryPositions={territoryPositions}
							equipmentPositions={equipmentPositions}
						/>
					)}

					{/* Territory polygons - each has distinct color, click to zoom */}
					{territories
						.filter(
							(
								t
							): t is Territory & {
								coordinates: NonNullable<Territory["coordinates"]>;
							} => !!t.coordinates?.coordinates?.length
						)
						.map((territory, index) => (
							<TerritoryPolygon
								key={territory.id}
								territory={territory}
								colorIndex={index}
								isSelected={selectedTerritory?.id === territory.id}
								onSelect={(t) => setSelectedTerritory(t)}
							>
								<Popup>
									<TerritoryPopup
										territory={territory}
										onEdit={(t) => setEditingTerritory(t)}
										onOpenChildren={openChildren}
									/>
								</Popup>
							</TerritoryPolygon>
						))}

					{/* Equipment markers */}
					{withPosition.map((eq) => (
						<Marker
							key={eq.equipmentId}
							position={[eq.position.latitude, eq.position.longitude]}
							icon={createCarIcon(eq.status, 28)}
						>
							<Popup>
								<div className="text-sm min-w-[160px]">
									<p className="font-semibold mb-1">{eq.equipmentName}</p>
									{eq.type && <p className="text-gray-500 text-xs mb-1">{eq.type.name}</p>}
									{eq.licensePlate && (
										<p className="mb-1">
											Raqam: <strong>{eq.licensePlate}</strong>
										</p>
									)}
									<p className="mb-1">
										Holat:{" "}
										<span
											style={{
												color: STATUS_COLORS[eq.status] ?? "#6b7280",
												fontWeight: 600,
											}}
										>
											{STATUS_LABELS[eq.status] ?? eq.status}
										</span>
									</p>
									{eq.assignedStaff && (
										<p className="mb-2">
											Haydovchi: <strong>{eq.assignedStaff.fullname}</strong>
										</p>
									)}
									<button
										type="button"
										className="text-blue-600 text-xs cursor-pointer bg-none border-none p-0 underline"
										onClick={() => navigate(`/equipment/${eq.equipmentId}`)}
									>
										GPS tarixini ko'rish →
									</button>
								</div>
							</Popup>
						</Marker>
					))}
				</MapContainer>
			</div>

			<TerritoryFormModal
				open={isCreateOpen || !!editingTerritory}
				onClose={() => {
					setIsCreateOpen(false);
					setEditingTerritory(null);
				}}
				organizationId={organizationId}
				territory={editingTerritory}
				defaultParentId={currentParentId}
			/>
		</div>
	);
}

function TerritoryPopup({
	territory,
	onEdit,
	onOpenChildren,
}: {
	territory: Territory;
	onEdit: (territory: Territory) => void;
	onOpenChildren: (territory: Territory) => void;
}) {
	const navigate = useNavigate();

	return (
		<div className="text-sm min-w-[200px]">
			<p className="font-semibold text-base mb-1">{territory.name}</p>
			<p className="text-gray-500 text-xs mb-1">{territory.organizationName}</p>
			<p className="mb-1">
				Yuzasi: <strong>{territory.area?.toLocaleString("uz-UZ") ?? "—"} m²</strong>
			</p>
			<p className="mb-1">
				Perimetr: <strong>{territory.perimeter?.toLocaleString("uz-UZ") ?? "—"} m</strong>
			</p>
			<p className="mb-1">
				Xodimlar: <strong>{territory.assignedStaffCount ?? 0}</strong>
			</p>
			<p className="mb-1">
				Vazifalar: <strong>{territory.taskCount ?? 0}</strong>
			</p>
			<p className="mb-1">
				Ekinlar: <strong>{territory.plantingCount ?? 0}</strong>
			</p>
			{territory.assignedBrigades && territory.assignedBrigades.length > 0 && (
				<p className="mb-1">
					Brigadalar: <strong>{territory.assignedBrigades.map((b) => b.name).join(", ")}</strong>
				</p>
			)}
			{territory.assignedStaff && territory.assignedStaff.length > 0 && (
				<div className="mt-2 pt-2 border-t border-gray-100">
					<p className="text-xs text-gray-500 mb-1">Biriktirilgan xodimlar:</p>
					<ul className="text-xs space-y-0.5">
						{territory.assignedStaff.map((s) => (
							<li key={s.id}>
								{s.fullname}
								{s.isPrimary && <span className="text-blue-600 ml-1">(asosiy)</span>}
							</li>
						))}
					</ul>
				</div>
			)}
			<div className="flex items-center gap-2 mt-3 pt-2 border-t border-gray-100">
				<Button
					size="small"
					type="link"
					className="p-0 text-xs"
					onClick={() => navigate(`/territories/${territory.id}`)}
				>
					Batafsil →
				</Button>
				<Button
					size="small"
					type="link"
					icon={<EditOutlined />}
					className="p-0 text-xs"
					onClick={() => onEdit(territory)}
				>
					Tahrirlash
				</Button>
				<Button
					size="small"
					type="link"
					icon={<FolderOpenOutlined />}
					className="p-0 text-xs"
					onClick={() => onOpenChildren(territory)}
				>
					Ichki hududlar
				</Button>
			</div>
		</div>
	);
}
